/**
 * twitter-identity-patch.ts — pure, hermetic identity-refresh logic (WS-K P1).
 *
 * Extracted from auth-service.getOrCreateUserDocument so it is trivially unit-testable
 * WITHOUT dragging in the firebase client SDK (auth-service.ts imports firebase at module
 * load). Mirrors the auth-roles.ts extraction pattern: pure functions only, no react / no
 * firebase / no next/*.
 *
 * WHY THIS EXISTS: a pre-existing account that predates the X sign-in fields — or one whose
 * pbs.twimg.com avatar URL has rotted — never self-heals, because the old code only wrote
 * Twitter fields on FIRST link (`!data.twitterId`). This computes the MINIMAL patch to apply
 * on EVERY Twitter sign-in: refresh the volatile identity fields when they changed, backfill
 * the one-time link fields when absent, and (THE INVARIANT) never clobber a user-chosen
 * profileImage. Returns an empty object when nothing moved, so the caller can skip the write.
 */

import type { TwitterAuthData } from './twitter-auth-service';

/** The stored user fields this patch reads (subset of the Firestore users/<uid> doc). */
export interface StoredIdentityFields {
  twitterId?: string;
  email?: string;
  profileImage?: string;
  twitterUsername?: string;
  twitterDisplayName?: string;
  twitterProfileImageUrl?: string;
}

/**
 * Compute the minimal Firestore patch to reconcile an existing user doc with a fresh Twitter
 * sign-in. Only keys whose value actually CHANGED are included; an empty object means "no write".
 */
export function computeTwitterIdentityPatch(
  existing: StoredIdentityFields,
  incoming: TwitterAuthData,
): Record<string, string> {
  const patch: Record<string, string> = {};

  // One-time link backfill — only ever SET, never overwritten once present.
  if (!existing.twitterId) {
    patch.authProvider = 'twitter';
    patch.twitterId = incoming.twitterId;
  }
  if (incoming.email && !existing.email) {
    patch.email = incoming.email;
  }

  // Volatile identity — refresh when X reports a different value than we last stored.
  if (incoming.twitterUsername && existing.twitterUsername !== incoming.twitterUsername) {
    patch.twitterUsername = incoming.twitterUsername;
  }
  if (
    incoming.twitterDisplayName &&
    existing.twitterDisplayName !== incoming.twitterDisplayName
  ) {
    patch.twitterDisplayName = incoming.twitterDisplayName;
  }
  if (
    incoming.twitterProfileImageUrl &&
    existing.twitterProfileImageUrl !== incoming.twitterProfileImageUrl
  ) {
    patch.twitterProfileImageUrl = incoming.twitterProfileImageUrl;
  }

  // Seed profileImage from the X avatar ONLY when the user has none. Never clobber a
  // user-chosen image (the profile editor writes profileImage directly).
  if (incoming.twitterProfileImageUrl && !existing.profileImage) {
    patch.profileImage = incoming.twitterProfileImageUrl;
  }

  return patch;
}
